import { Link } from "wouter";
import { SEOHead, ORGANIZATION_SCHEMA } from "@/components/seo-head";
import { FAQ_PAGES } from "@/lib/faq-data";
import { Button } from "@/components/ui/button";
import { SeoPageHeader } from "@/components/seo-page-header";
import { SeoPageFooter } from "@/components/seo-page-footer";
import { ChevronRight, HelpCircle } from "lucide-react";

const FAQ_INDEX_URL = "https://thepropertool.com/faq/";

const FAQ_PAGE_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": FAQ_PAGES.flatMap((page) =>
    page.faqs.map((faq) => ({
      "@type": "Question",
      "name": faq.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": faq.a,
      },
    }))
  ),
};

const ITEM_LIST_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "ItemList",
  "name": "The Proper Tool FAQ",
  "description": "Common questions from UK builders about AI estimating and quoting software.",
  "url": FAQ_INDEX_URL,
  "itemListElement": FAQ_PAGES.map((page, index) => ({
    "@type": "ListItem",
    "position": index + 1,
    "url": `https://thepropertool.com/faq/${page.slug}/`,
    "name": page.title,
  })),
};

const BREADCRUMB_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "BreadcrumbList",
  "itemListElement": [
    {
      "@type": "ListItem",
      "position": 1,
      "name": "Home",
      "item": "https://thepropertool.com",
    },
    {
      "@type": "ListItem",
      "position": 2,
      "name": "FAQ",
      "item": FAQ_INDEX_URL,
    },
  ],
};

function formatDate(iso: string) {
  return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "long", year: "numeric" });
}

export default function FaqIndexPage() {
  return (
    <div className="min-h-screen bg-background text-foreground">
      <SEOHead
        title="FAQ: AI Estimating and Quoting for UK Builders | The Proper Tool"
        description="Answers to the most common questions UK builders have about AI estimating, quoting software, accuracy, and how The Proper Tool works."
        canonical="/faq"
        ogType="website"
        schema={[FAQ_PAGE_SCHEMA, ITEM_LIST_SCHEMA, BREADCRUMB_SCHEMA, ORGANIZATION_SCHEMA]}
      />

      <SeoPageHeader />

      <main className="max-w-6xl mx-auto px-4 sm:px-6 py-12 sm:py-16">
        <div className="mb-12">
          <div className="flex items-center gap-2 text-sm text-muted-foreground mb-3">
            <Link href="/" className="hover:text-foreground transition-colors">Home</Link>
            <ChevronRight className="h-4 w-4" />
            <span>FAQ</span>
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold mb-4">Frequently Asked Questions</h1>
          <p className="text-lg text-muted-foreground max-w-2xl">
            Common questions from UK builders about AI estimating, quoting software, accuracy, and how The Proper Tool works.
          </p>
        </div>

        <section className="mb-16">
          <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
            {FAQ_PAGES.map((page) => (
              <article key={page.slug} className="group border border-border rounded-xl hover:border-primary/50 hover:shadow-md transition-all flex flex-col">
                <Link href={`/faq/${page.slug}`} className="flex flex-col flex-1 p-6">
                  <div className="mb-3">
                    <span className="text-xs font-medium px-2 py-0.5 rounded-full bg-primary/10 text-primary">FAQ</span>
                  </div>
                  <h2 className="font-semibold text-lg leading-snug mb-3 group-hover:text-primary transition-colors">
                    {page.title}
                  </h2>
                  <p className="text-sm text-muted-foreground leading-relaxed flex-1">{page.excerpt}</p>
                  <div className="flex items-center justify-between mt-4">
                    <span className="text-xs text-muted-foreground">{formatDate(page.publishedDate)}</span>
                    <div className="flex items-center gap-1 text-sm font-medium text-primary">
                      Read article <ChevronRight className="h-4 w-4" />
                    </div>
                  </div>
                </Link>
              </article>
            ))}
          </div>
        </section>

        <section className="bg-primary/5 border border-primary/20 rounded-2xl p-8 text-center">
          <HelpCircle className="h-8 w-8 text-primary mx-auto mb-4" />
          <h2 className="text-2xl font-bold mb-3">Still have questions?</h2>
          <p className="text-muted-foreground mb-6 max-w-lg mx-auto">
            Try The Proper Tool free for 14 days. Generate a complete, line-item quote from drawings or a plain-text description in under 10 minutes.
          </p>
          <Button size="lg" asChild>
            <Link href="/login">Start free, no card needed</Link>
          </Button>
        </section>
      </main>

      <SeoPageFooter />
    </div>
  );
}
